import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  Wifi,
  WifiOff,
  Trash2,
  QrCode,
  Linkedin,
  MessageSquare,
  RefreshCw,
  Save,
  CheckCircle2,
  Clock,
  Webhook,
  Copy,
  RotateCcw,
  AlertTriangle,
  Pencil,
  Check,
  X,
} from "lucide-react";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const STATUS_LABEL = {
  connected: { label: "Conectado", color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  active: { label: "Ativo", color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  qr: { label: "Aguardando QR", color: "text-amber-600 bg-amber-50 border-amber-200" },
  pending: { label: "Pendente", color: "text-slate-500 bg-slate-50 border-slate-200" },
  disconnected: { label: "Desconectado", color: "text-red-600 bg-red-50 border-red-200" },
  connecting: { label: "Conectando…", color: "text-blue-600 bg-blue-50 border-blue-200" },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || { label: status, color: "text-slate-500 bg-slate-50 border-slate-200" };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${s.color}`}>
      {s.label}
    </span>
  );
}

export default function SynklyAccountsPanel() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ invitation_auto_message_enabled: false, invitation_auto_message: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  // WhatsApp QR state
  const [qrData, setQrData] = useState(null); // { accountId, qr }
  const [connectingWA, setConnectingWA] = useState(false);
  const [waLabel, setWaLabel] = useState("WhatsApp Principal");
  const pollRef = useRef(null);

  // LinkedIn token state
  const [generatingToken, setGeneratingToken] = useState(false);
  const [liToken, setLiToken] = useState(null); // { token, expiresAt }
  const [tokenCopied, setTokenCopied] = useState(false);

  // Webhook state
  const [webhookStatus, setWebhookStatus] = useState(null); // { url, registered }
  const [registeringWebhook, setRegisteringWebhook] = useState(false);

  const load = useCallback(async () => {
    try {
      const [accRes, settRes, statusRes] = await Promise.all([
        axios.get(`${API}/integrations/synkly/accounts`),
        axios.get(`${API}/integrations/synkly/settings`),
        axios.get(`${API}/integrations/synkly/status`),
      ]);
      setAccounts(accRes.data || []);
      setSettings(settRes.data || {});
      const st = statusRes.data || {};
      setWebhookStatus({
        url: st.webhook_url || null,
        registered: null, // unknown until we list
      });
    } catch {
      toast.error("Não foi possível carregar contas Synkly");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRegisterWebhook = async () => {
    setRegisteringWebhook(true);
    try {
      const res = await axios.post(`${API}/integrations/synkly/webhooks/register`);
      const results = res.data?.results || [];
      const ok = results.filter((r) => r.status === "registered");
      const err = results.filter((r) => r.status === "error");
      if (ok.length > 0) {
        toast.success(`Webhook registrado com sucesso!`);
      }
      if (err.length > 0) {
        toast.error(`Erro ao registrar: ${err[0]?.detail || "verifique os logs"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar webhook");
    } finally {
      setRegisteringWebhook(false);
    }
  };

  // Auto-refresh when any account is in a transient state (connecting / qr / pending)
  const statusPollRef = useRef(null);
  useEffect(() => {
    load();
    return () => {
      clearInterval(pollRef.current);
      clearInterval(statusPollRef.current);
    };
  }, [load]);

  useEffect(() => {
    clearInterval(statusPollRef.current);
    const hasTransient = accounts.some((a) =>
      ["connecting", "qr", "pending"].includes(a.status)
    );
    if (hasTransient) {
      statusPollRef.current = setInterval(load, 5000);
    }
    return () => clearInterval(statusPollRef.current);
  }, [accounts, load]);

  // ── WhatsApp connection ───────────────────────────────────────────────────

  const startQrPoll = useCallback((accountId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/integrations/synkly/accounts/${accountId}`);
        const acc = res.data;
        if (acc.status === "qr" && acc.qr) {
          // QR code ready — show the modal
          setQrData({ accountId, qr: acc.qr });
        } else if (acc.status === "connected" || acc.status === "active") {
          // Successfully connected
          clearInterval(pollRef.current);
          setQrData(null);
          setConnectingWA(false);
          toast.success("WhatsApp conectado com sucesso!");
          load();
        } else if (acc.status === "disconnected" || acc.status === "error" || acc.status === "failed") {
          // Terminal failure
          clearInterval(pollRef.current);
          setQrData(null);
          setConnectingWA(false);
          toast.error("Falha ao conectar WhatsApp. Tente novamente.");
          load();
        }
        // Otherwise (connecting, pending, qr without image yet) — keep polling
      } catch {
        clearInterval(pollRef.current);
        setQrData(null);
        setConnectingWA(false);
      }
    }, 3000);
  }, [load]);

  const handleConnectWhatsApp = async () => {
    if (!waLabel.trim()) { toast.error("Informe um nome para a conta"); return; }
    setConnectingWA(true);
    try {
      const res = await axios.post(`${API}/integrations/synkly/accounts/whatsapp`, { label: waLabel.trim() });
      const acc = res.data;
      if (acc.status === "qr" && acc.qr) {
        // Synkly returned QR immediately
        setQrData({ accountId: acc.id, qr: acc.qr });
        startQrPoll(acc.id);
      } else if (acc.id && (acc.status === "connecting" || acc.status === "pending" || acc.status === "qr")) {
        // Synkly is generating the QR — start polling until it appears
        toast.info("Gerando QR code, aguarde alguns segundos…");
        startQrPoll(acc.id);
      } else {
        setConnectingWA(false);
        toast.success("Conta criada! Status: " + (acc.status || "ok"));
        load();
      }
    } catch (err) {
      setConnectingWA(false);
      toast.error(err?.response?.data?.detail || "Erro ao conectar WhatsApp");
    }
  };

  const cancelQr = () => {
    clearInterval(pollRef.current);
    setQrData(null);
    setConnectingWA(false);
  };

  // Force Synkly to re-initialise the WhatsApp session and get a new QR
  const handleReconnectWhatsApp = useCallback(async (accountId) => {
    toast.info("Reiniciando sessão WhatsApp…");
    try {
      const res = await axios.post(`${API}/integrations/synkly/accounts/${accountId}/reconnect`);
      const acc = res.data;
      if (acc.status === "qr" && acc.qr) {
        setQrData({ accountId, qr: acc.qr });
        startQrPoll(accountId);
      } else if (acc.status === "connected" || acc.status === "active") {
        toast.success("WhatsApp reconectado com sucesso!");
        load();
      } else {
        // Session restarting — poll until QR or connected
        toast.info("Aguardando novo QR code…");
        startQrPoll(accountId);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao reconectar — tente desconectar e conectar novamente");
    }
  }, [load, startQrPoll]);

  // ── LinkedIn token generation ─────────────────────────────────────────────

  const handleGenerateToken = async () => {
    setGeneratingToken(true);
    setLiToken(null);
    try {
      const res = await axios.post(`${API}/integrations/synkly/accounts/linkedin/connect-token`, {
        label: "LinkedIn",
      });
      setLiToken(res.data);
      toast.success("Token gerado! Cole-o na extensão Chrome do Synkly.");
      // Start polling to detect when the extension connects the account
      statusPollRef.current = setInterval(load, 5000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao gerar token");
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleCopyToken = () => {
    if (!liToken?.token) return;
    navigator.clipboard.writeText(liToken.token).then(() => {
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2500);
    });
  };

  // ── Disconnect ────────────────────────────────────────────────────────────

  const handleDisconnect = async (accountId) => {
    if (!window.confirm("Desconectar esta conta?")) return;
    try {
      await axios.delete(`${API}/integrations/synkly/accounts/${accountId}`);
      toast.success("Conta desconectada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao desconectar");
    }
  };

  // ── Rename label ─────────────────────────────────────────────────────────

  const [editingLabel, setEditingLabel] = useState(null); // { accountId, value }

  const startEditLabel = (acc) =>
    setEditingLabel({ accountId: acc.synkly_account_id, value: acc.label || "" });

  const handleRenameAccount = async () => {
    if (!editingLabel) return;
    const { accountId, value } = editingLabel;
    if (!value.trim()) { toast.error("Nome não pode ser vazio"); return; }
    try {
      await axios.patch(`${API}/integrations/synkly/accounts/${accountId}/label`, { label: value.trim() });
      toast.success("Nome atualizado");
      setEditingLabel(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao renomear");
    }
  };

  // ── Settings (LinkedIn auto-message) ─────────────────────────────────────

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/integrations/synkly/settings`, settings);
      toast.success("Configurações salvas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSavingSettings(false);
    }
  };

  const waAccounts = accounts.filter((a) => a.channel === "whatsapp");
  const liAccounts = accounts.filter((a) => a.channel === "linkedin");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── QR Code Modal ─────────────────────────────────────────────────── */}
      {qrData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 text-center space-y-4">
            <div className="flex items-center justify-center gap-2 text-slate-800 font-semibold text-lg">
              <QrCode className="w-5 h-5" />
              Escaneie o QR code
            </div>
            <p className="text-sm text-slate-500">
              Abra o WhatsApp no celular → Dispositivos conectados → Conectar dispositivo
            </p>
            <div className="flex justify-center">
              <img
                src={qrData.qr}
                alt="QR Code WhatsApp"
                className="w-56 h-56 rounded-lg border border-slate-200"
              />
            </div>
            <div className="flex items-center justify-center gap-1.5 text-xs text-amber-600">
              <Clock className="w-3.5 h-3.5" />
              Aguardando conexão…
            </div>
            <Button variant="outline" size="sm" onClick={cancelQr} className="w-full">
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* ── WhatsApp ──────────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-emerald-600" />
          <h2 className="text-base font-semibold text-slate-900">WhatsApp via Synkly</h2>
        </div>

        {waAccounts.length > 0 && (
          <div className="space-y-2">
            {/* Warning banner when WhatsApp is not connected */}
            {waAccounts.some((a) => !["connected", "active"].includes(a.status)) && (
              <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Sessão WhatsApp interrompida. Reconecte a conta para voltar a enviar mensagens.
                  {waAccounts.some((a) => a.status === "connecting") && " Tentando reconectar automaticamente…"}
                </span>
              </div>
            )}
            {waAccounts.map((acc) => {
              const isEditing = editingLabel?.accountId === acc.synkly_account_id;
              return (
                <div
                  key={acc.synkly_account_id}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 border border-slate-200"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {acc.status === "connected" || acc.status === "active" ? (
                      <Wifi className="w-4 h-4 text-emerald-500 shrink-0" />
                    ) : acc.status === "connecting" ? (
                      <Loader2 className="w-4 h-4 text-blue-400 animate-spin shrink-0" />
                    ) : (
                      <WifiOff className="w-4 h-4 text-red-400 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            autoFocus
                            value={editingLabel.value}
                            onChange={(e) => setEditingLabel((p) => ({ ...p, value: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") handleRenameAccount(); if (e.key === "Escape") setEditingLabel(null); }}
                            className="text-sm font-medium border border-blue-400 rounded px-2 py-0.5 w-40 outline-none focus:ring-1 focus:ring-blue-400"
                          />
                          <button onClick={handleRenameAccount} className="p-1 text-emerald-600 hover:text-emerald-700"><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setEditingLabel(null)} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium text-slate-800 truncate">{acc.label}</p>
                          <button onClick={() => startEditLabel(acc)} className="p-0.5 text-slate-300 hover:text-blue-500 transition" title="Renomear"><Pencil className="w-3 h-3" /></button>
                        </div>
                      )}
                      <p className="text-xs text-slate-400 font-mono truncate">{acc.synkly_account_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={acc.status} />
                    <button
                      onClick={() => handleReconnectWhatsApp(acc.synkly_account_id)}
                      className="p-1.5 text-slate-400 hover:text-blue-600 transition rounded"
                      title="Forçar reconexão"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDisconnect(acc.synkly_account_id)}
                      className="p-1.5 text-slate-400 hover:text-red-500 transition rounded"
                      title="Desconectar"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="wa-label" className="text-xs">Nome da conta</Label>
            <Input
              id="wa-label"
              value={waLabel}
              onChange={(e) => setWaLabel(e.target.value)}
              placeholder="Ex: WhatsApp Comercial"
              className="h-9 text-sm"
              disabled={connectingWA}
            />
          </div>
          <Button
            onClick={handleConnectWhatsApp}
            disabled={connectingWA}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4 shrink-0"
          >
            {connectingWA ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Aguardando QR…</>
            ) : (
              <><QrCode className="w-4 h-4 mr-1.5" />Conectar WhatsApp</>
            )}
          </Button>
        </div>

        <p className="text-xs text-slate-500 leading-relaxed">
          Conecte o WhatsApp Business via QR Code. Mensagens da plataforma usarão esta conta automaticamente.
        </p>
      </div>

      {/* ── LinkedIn ──────────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
        <div className="flex items-center gap-2">
          <Linkedin className="w-5 h-5 text-blue-600" />
          <h2 className="text-base font-semibold text-slate-900">LinkedIn via Synkly</h2>
        </div>

        {/* Connected accounts list */}
        {liAccounts.length > 0 && (
          <div className="space-y-2">
            {liAccounts.map((acc) => {
              const isEditing = editingLabel?.accountId === acc.synkly_account_id;
              return (
                <div
                  key={acc.synkly_account_id}
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 border border-slate-200"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {acc.status === "connected" || acc.status === "active" ? (
                      <CheckCircle2 className="w-4 h-4 text-blue-500 shrink-0" />
                    ) : acc.status === "pending" ? (
                      <Loader2 className="w-4 h-4 text-amber-400 animate-spin shrink-0" />
                    ) : (
                      <WifiOff className="w-4 h-4 text-slate-400 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            autoFocus
                            value={editingLabel.value}
                            onChange={(e) => setEditingLabel((p) => ({ ...p, value: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") handleRenameAccount(); if (e.key === "Escape") setEditingLabel(null); }}
                            className="text-sm font-medium border border-blue-400 rounded px-2 py-0.5 w-48 outline-none focus:ring-1 focus:ring-blue-400"
                          />
                          <button onClick={handleRenameAccount} className="p-1 text-emerald-600 hover:text-emerald-700"><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setEditingLabel(null)} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium text-slate-800 truncate">{acc.label}</p>
                          <button onClick={() => startEditLabel(acc)} className="p-0.5 text-slate-300 hover:text-blue-500 transition" title="Renomear"><Pencil className="w-3 h-3" /></button>
                        </div>
                      )}
                      <p className="text-xs text-slate-400 font-mono truncate">{acc.synkly_account_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge status={acc.status} />
                    <button
                      onClick={() => handleDisconnect(acc.synkly_account_id)}
                      className="p-1.5 text-slate-400 hover:text-red-500 transition rounded"
                      title="Desconectar"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Token generation form */}
        <div className="space-y-3">
          <div>
            <Button
              onClick={handleGenerateToken}
              disabled={generatingToken}
              className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-4"
            >
              {generatingToken ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Gerando…</>
              ) : (
                <><Linkedin className="w-4 h-4 mr-1.5" />Gerar Token</>
              )}
            </Button>
          </div>

          {/* Token display — shown after generation */}
          {liToken && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-blue-800 mb-1.5">Token gerado — cole na extensão Chrome do Synkly</p>
                  <div className="flex items-center gap-2 bg-white rounded border border-blue-200 px-3 py-2">
                    <span className="font-mono text-sm text-slate-700 truncate flex-1 select-all">
                      {liToken.token}
                    </span>
                    <button
                      onClick={handleCopyToken}
                      className="shrink-0 text-blue-600 hover:text-blue-800 transition"
                      title="Copiar token"
                    >
                      {tokenCopied
                        ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                        : <Copy className="w-4 h-4" />
                      }
                    </button>
                  </div>
                </div>
              </div>
              {liToken.expiresAt && (
                <div className="flex items-center gap-1.5 text-xs text-blue-700">
                  <Clock className="w-3.5 h-3.5 shrink-0" />
                  Válido até {new Date(liToken.expiresAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} — use na extensão antes de expirar
                </div>
              )}
              <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside leading-relaxed">
                <li>Instale a extensão Synkly no Google Chrome</li>
                <li>Na extensão, cole o token acima no campo indicado</li>
                <li>Acesse o LinkedIn — a extensão capturará suas credenciais automaticamente</li>
                <li>Aguarde a conta aparecer como <strong>Conectado</strong> acima</li>
              </ol>
            </div>
          )}

          {!liToken && (
            <p className="text-xs text-slate-500 leading-relaxed">
              Gere um token e cole-o na extensão Chrome do Synkly. A extensão captura suas credenciais
              do LinkedIn automaticamente — sem precisar copiar cookies manualmente.
            </p>
          )}
        </div>

        {/* LinkedIn invitation auto-message */}
        <div className="border-t border-slate-100 pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-800">
                Mensagem automática após aceite de convite
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Enviada automaticamente quando um candidato aceitar seu convite de conexão no LinkedIn.
              </p>
            </div>
            <Switch
              checked={settings.invitation_auto_message_enabled}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, invitation_auto_message_enabled: v }))}
            />
          </div>
          {settings.invitation_auto_message_enabled && (
            <Textarea
              value={settings.invitation_auto_message}
              onChange={(e) => setSettings((s) => ({ ...s, invitation_auto_message: e.target.value }))}
              placeholder="Olá! Obrigado por aceitar meu convite. Gostaria de conversar sobre uma oportunidade que pode ser do seu interesse..."
              rows={4}
              className="text-sm resize-none"
            />
          )}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveSettings}
              disabled={savingSettings}
              size="sm"
              variant="outline"
              className="gap-1.5"
            >
              {savingSettings ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Save className="w-3.5 h-3.5" />
              )}
              Salvar configurações LinkedIn
            </Button>
          </div>
        </div>
      </div>

      {/* ── Webhook ───────────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Webhook className="w-5 h-5 text-slate-600" />
          <h2 className="text-base font-semibold text-slate-900">Webhook de mensagens recebidas</h2>
        </div>

        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            Para receber respostas de WhatsApp no Grid, a Synkly precisa saber para onde enviar as mensagens.
            Clique em <strong>Registrar Webhook</strong> para configurar isso automaticamente.
            Faça isso sempre que mudar o endereço do servidor ou a chave de assinatura.
          </p>
        </div>

        {webhookStatus?.url && (
          <div className="space-y-1.5">
            <Label className="text-xs">URL do Webhook (configure na Synkly)</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 font-mono truncate text-slate-700">
                {webhookStatus.url}
              </code>
              <button
                onClick={() => { navigator.clipboard.writeText(webhookStatus.url); toast.success("URL copiada!"); }}
                className="p-2 text-slate-400 hover:text-slate-700 transition rounded border border-slate-200"
                title="Copiar URL"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        <Button
          onClick={handleRegisterWebhook}
          disabled={registeringWebhook}
          size="sm"
          className="bg-slate-800 hover:bg-slate-900 text-white gap-1.5"
        >
          {registeringWebhook ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" />Registrando…</>
          ) : (
            <><RotateCcw className="w-3.5 h-3.5" />Registrar / Atualizar Webhook</>
          )}
        </Button>
      </div>

      {/* ── Refresh ───────────────────────────────────────────────────────── */}
      <div className="flex justify-end">
        <button
          onClick={() => { setLoading(true); load(); }}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Atualizar status das contas
        </button>
      </div>
    </div>
  );
}
